import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types } from "mobx-state-tree";
import type { Tables } from "@/types/supabase-helpers";

// Database row types
// BuildingDataAttributeRow is manually typed (data schema, not in generated types)
export type BuildingDataAttributeRow = {
  group_id: number;
  group_nm: string;
  attribute_nm: string;
  name_en: string | null;
  name_et: string | null;
  definition_en: string | null;
  default_unit: string | null;
  attribute_type_en: string | null;
  attribute_type_et: string | null;
  value_type: string | null;
  derived_ind: boolean | null;
  attribute_formula: string | null;
  attribute_formal_def: string | null;
  display_on_widget: boolean;
  sort_order: number | null;
  created_at: string;
  created_by: number;
  modified_at: string;
  modified_by: number;
};
export type BuildingDataAttributeInsert = Partial<BuildingDataAttributeRow> &
  Pick<BuildingDataAttributeRow, "attribute_nm" | "group_id" | "group_nm">;
export type BuildingDataAttributeUpdate = Partial<BuildingDataAttributeRow>;

// Class Category (data.def_class_category)
export type ClassCategoryRow = {
  category_id: number;
  class_category: string;
  created_at: string;
  modified_at: string;
};
export type ClassCategoryInsert = Pick<ClassCategoryRow, "category_id" | "class_category">;
export type ClassCategoryUpdate = Partial<Pick<ClassCategoryRow, "class_category">>;

// Sub-Class (data.def_sub_class)
export type SubClassRow = {
  id: number;
  sub_class: string;
  sub_class_second: string | null;
  category_id: number | null;
  created_at: string;
  modified_at: string;
};
export type SubClassInsert = Omit<SubClassRow, "id" | "created_at" | "modified_at">;
export type SubClassUpdate = Partial<
  Pick<SubClassRow, "sub_class" | "sub_class_second" | "category_id">
>;

// Attribute ↔ Sub-Class junction (data.def_attribute_sub_class)
export type AttributeSubClassRow = {
  attribute_nm: string;
  sub_class_id: number;
  created_at: string;
  modified_at: string;
};

// Attribute Validation (data.def_attribute_validation)
export type AttributeValidationRow = {
  id: number;
  attribute_nm: string;
  validation_type: "enum" | "enum_multiselect" | "range" | "length" | "pattern";
  validation_value: string;
  // Estonian display translation for enum values; null falls back to validation_value.
  validation_value_et: string | null;
  sort_order: number;
  created_at: string;
  modified_at: string;
};
export type AttributeValidationInsert = Omit<
  AttributeValidationRow,
  "id" | "created_at" | "modified_at"
>;
export type AttributeValidationUpdate = Partial<
  Pick<AttributeValidationRow, "validation_value" | "validation_value_et" | "sort_order">
>;

export type DocumentCategoryRow = Tables<"document_categories">;
export type DesignDisciplineRow = Tables<"design_disciplines">;
export type DesignStageRow = Tables<"design_stages">;
export type DocumentTemplateRow = Tables<"document_templates">;
export type PipelineRow = Tables<"pipelines">;
export type RoleRow = Tables<"roles">;
export type PermissionRow = Tables<"permissions">;
export type RolePermissionRow = Tables<"role_permissions">;

// Building Data Attribute model (data.def_building_data_model)
export const BuildingDataAttribute = types.model("BuildingDataAttribute", {
  attribute_nm: types.identifier,
  group_id: types.number,
  group_nm: types.string,
  name_en: types.maybeNull(types.string),
  name_et: types.maybeNull(types.string),
  definition_en: types.maybeNull(types.string),
  default_unit: types.maybeNull(types.string),
  attribute_type_en: types.maybeNull(types.string),
  attribute_type_et: types.maybeNull(types.string),
  value_type: types.maybeNull(types.string),
  derived_ind: types.maybeNull(types.boolean),
  attribute_formula: types.maybeNull(types.string),
  attribute_formal_def: types.maybeNull(types.string),
  display_on_widget: types.boolean,
  sort_order: types.maybeNull(types.number),
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type BuildingDataAttributeInstance = Instance<typeof BuildingDataAttribute>;
export type BuildingDataAttributeSnapshotIn = SnapshotIn<typeof BuildingDataAttribute>;
export type BuildingDataAttributeSnapshotOut = SnapshotOut<typeof BuildingDataAttribute>;

// Class Category model (data.def_class_category)
export const ClassCategory = types.model("ClassCategory", {
  category_id: types.identifierNumber,
  class_category: types.string,
  created_at: types.string,
  modified_at: types.string,
});

export type ClassCategoryInstance = Instance<typeof ClassCategory>;
export type ClassCategorySnapshotIn = SnapshotIn<typeof ClassCategory>;
export type ClassCategorySnapshotOut = SnapshotOut<typeof ClassCategory>;

// Sub-Class model (data.def_sub_class)
export const SubClass = types.model("SubClass", {
  id: types.identifierNumber,
  sub_class: types.string,
  sub_class_second: types.maybeNull(types.string),
  category_id: types.maybeNull(types.number),
  created_at: types.string,
  modified_at: types.string,
});

export type SubClassInstance = Instance<typeof SubClass>;
export type SubClassSnapshotIn = SnapshotIn<typeof SubClass>;
export type SubClassSnapshotOut = SnapshotOut<typeof SubClass>;

// Document Category model
export const DocumentCategory = types.model("DocumentCategory", {
  id: types.identifierNumber,
  category_name: types.string,
  category_type: types.string,
  description: types.maybeNull(types.string),
  name_et: types.maybeNull(types.string),
  parent_category_id: types.maybeNull(types.number),
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type DocumentCategoryInstance = Instance<typeof DocumentCategory>;
export type DocumentCategorySnapshotIn = SnapshotIn<typeof DocumentCategory>;
export type DocumentCategorySnapshotOut = SnapshotOut<typeof DocumentCategory>;

// Design Discipline model
export const DesignDiscipline = types.model("DesignDiscipline", {
  id: types.identifierNumber,
  name: types.string,
  name_en: types.maybeNull(types.string),
  abbreviation: types.maybeNull(types.string),
  abbreviation_en: types.maybeNull(types.string),
  group_code: types.maybeNull(types.string),
  group_code_en: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type DesignDisciplineInstance = Instance<typeof DesignDiscipline>;
export type DesignDisciplineSnapshotIn = SnapshotIn<typeof DesignDiscipline>;
export type DesignDisciplineSnapshotOut = SnapshotOut<typeof DesignDiscipline>;

// Design Stage model
export const DesignStage = types.model("DesignStage", {
  id: types.identifierNumber,
  name: types.string,
  name_en: types.maybeNull(types.string),
  abbreviation: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  display_order: types.maybeNull(types.number),
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type DesignStageInstance = Instance<typeof DesignStage>;
export type DesignStageSnapshotIn = SnapshotIn<typeof DesignStage>;
export type DesignStageSnapshotOut = SnapshotOut<typeof DesignStage>;

// Document template model — the "New document" template catalogue.
export const DocumentTemplate = types.model("DocumentTemplate", {
  id: types.identifierNumber,
  // Matches document_forms.template_id and the frontend DocumentTemplateId.
  template_key: types.string,
  name: types.string,
  name_et: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  is_active: types.boolean,
  display_order: types.maybeNull(types.number),
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type DocumentTemplateInstance = Instance<typeof DocumentTemplate>;
export type DocumentTemplateSnapshotIn = SnapshotIn<typeof DocumentTemplate>;
export type DocumentTemplateSnapshotOut = SnapshotOut<typeof DocumentTemplate>;

// A template's classification link, with the category and stage names already resolved
// by the repository — the table renders names, never ids.
export const TemplateLink = types.model("TemplateLink", {
  id: types.identifierNumber,
  template_id: types.number,
  template_key: types.string,
  template_name: types.string,
  category_id: types.number,
  category_name: types.string,
  category_mode: types.string,
  stage_id: types.maybeNull(types.number),
  stage_name: types.maybeNull(types.string),
  feed_form_values: types.boolean,
});

export type TemplateLinkInstance = Instance<typeof TemplateLink>;
export type TemplateLinkSnapshotIn = SnapshotIn<typeof TemplateLink>;

// Pipeline model
export const Pipeline = types.model("Pipeline", {
  id: types.identifierNumber,
  uuid: types.string,
  name: types.string,
  slug: types.string,
  created_at: types.string,
  modified_at: types.string,
  created_by: types.number,
  modified_by: types.number,
});

export type PipelineInstance = Instance<typeof Pipeline>;
export type PipelineSnapshotIn = SnapshotIn<typeof Pipeline>;
export type PipelineSnapshotOut = SnapshotOut<typeof Pipeline>;

// Prompt model
export const Prompt = types.model("Prompt", {
  id: types.identifierNumber,
  slug: types.string,
  prompt_addition: types.maybeNull(types.string),
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type PromptRow = Tables<"prompts">;
export type PromptInstance = Instance<typeof Prompt>;
export type PromptSnapshotIn = SnapshotIn<typeof Prompt>;
export type PromptSnapshotOut = SnapshotOut<typeof Prompt>;

// Prompt Mode model
export const PromptMode = types.model("PromptMode", {
  id: types.identifierNumber,
  prompt_id: types.number,
  mode: types.string,
  system_prompt: types.string,
  created_at: types.string,
  created_by: types.number,
  modified_at: types.string,
  modified_by: types.number,
});

export type PromptModeRow = Tables<"prompt_modes">;
export type PromptModeInstance = Instance<typeof PromptMode>;
export type PromptModeSnapshotIn = SnapshotIn<typeof PromptMode>;
export type PromptModeSnapshotOut = SnapshotOut<typeof PromptMode>;

// Role model (public.roles) — joined with the role's permission codes so the
// store can keep the full picture in one observable.
export const Role = types.model("Role", {
  code: types.identifier,
  name: types.string,
  description: types.maybeNull(types.string),
  scope: types.string,
  deprecated: types.boolean,
  created_at: types.string,
  permissionCodes: types.array(types.string),
});

export type RoleInstance = Instance<typeof Role>;
export type RoleSnapshotIn = SnapshotIn<typeof Role>;
export type RoleSnapshotOut = SnapshotOut<typeof Role>;
